/**
 * Navigation Service for React Router 7
 * 
 * This service provides a way to navigate programmatically from outside React components
 * (e.g., Redux action creators, API handlers, etc.)
 * 
 * Since React Router 7 uses hooks for navigation and we can't use hooks in action creators,
 * we need to set up the navigate function from a component and expose it via this service.
 */

let navigateFunction = null;

/**
 * Set the navigate function - should be called from a component that has access to useNavigate
 * @param {Function} navigate - The navigate function from useNavigate()
 */
export function setNavigate(navigate) {
  navigateFunction = navigate;
}

/**
 * Navigate to a path - equivalent to history.push()
 * @param {string} path - The path to navigate to
 * @param {object} options - Navigation options (state, replace, etc.)
 */
export function navigate(path, options = {}) {
  if (navigateFunction) {
    navigateFunction(path, options);
  } else {
    // Fallback to window.location if navigate hasn't been set up yet
    // eslint-disable-next-line no-console
    console.warn('Navigation service not initialized, falling back to window.location');
    window.location.href = path;
  }
}

/**
 * Replace current location - equivalent to history.replace()
 * @param {string} path - The path to navigate to
 */
export function replace(path) {
  navigate(path, { replace: true });
}

/**
 * Go back in history - equivalent to history.goBack()
 */
export function goBack() {
  if (navigateFunction) {
    navigateFunction(-1);
  } else {
    window.history.back();
  }
}

/**
 * Go forward in history - equivalent to history.goForward()
 */
export function goForward() {
  if (navigateFunction) {
    navigateFunction(1);
  } else {
    window.history.forward();
  }
}

// Redux action creator compatible functions
// These return thunks that can be dispatched but actually just call the navigation service

/**
 * Redux-compatible push action creator
 * Use this as a drop-in replacement for react-router-redux's push
 */
export function push(path) {
  return () => {
    navigate(path);
  };
}

/**
 * Redux-compatible replace action creator
 * Use this as a drop-in replacement for react-router-redux's replace  
 */
export function replaceAction(path) {
  return () => {
    replace(path);
  };
}

export default {
  setNavigate,
  navigate,
  replace,
  goBack,
  goForward,
  push,
  replaceAction,
};
